/**
 * Advanced Beat Detection System
 * Analyzes audio to detect kicks, snares, and create beat grid
 */

export class BeatDetector {
  constructor(audioContext) {
    this.audioContext = audioContext;
    this.beatGrid = [];
    this.kickTimes = [];
    this.snareTimes = [];
    this.isAnalyzing = false;
  }

  /**
   * Analyze audio file to detect beats
   */
  async analyzeTrack(audioBuffer, bpm) {
    console.log('🔍 Starting beat detection...');
    this.isAnalyzing = true;
    
    try {
      const channelData = audioBuffer.getChannelData(0); // Use left channel
      const sampleRate = audioBuffer.sampleRate;
      const duration = audioBuffer.duration;
      
      // Calculate expected beat length
      const beatLength = 60 / bpm; // seconds per beat
      const samplesPerBeat = beatLength * sampleRate;
      
      console.log(`  📊 Track: ${duration.toFixed(1)}s, BPM: ${bpm}, Beat Length: ${beatLength.toFixed(3)}s`);
      
      // Step 1: Calculate energy in frequency bands
      const { lowEnergy, midEnergy, highEnergy } = this.calculateEnergyBands(channelData, sampleRate);
      
      // Step 2: Detect kicks (low frequency peaks)
      this.kickTimes = this.detectKicks(lowEnergy, sampleRate, beatLength);
      
      // Step 3: Detect snares (mid-high frequency peaks)
      this.snareTimes = this.detectSnares(midEnergy, highEnergy, sampleRate, beatLength);
      
      // Step 4: Create beat grid from kicks
      this.beatGrid = this.createBeatGrid(this.kickTimes, beatLength, duration);
      
      console.log(`  ✓ Detected ${this.kickTimes.length} kicks, ${this.snareTimes.length} snares`);
      console.log(`  ✓ Beat grid: ${this.beatGrid.length} beats`);
      
      this.isAnalyzing = false;
      return {
        kickTimes: this.kickTimes,
        snareTimes: this.snareTimes,
        beatGrid: this.beatGrid,
        success: true
      };
      
    } catch (error) {
      console.error('❌ Beat detection error:', error);
      this.isAnalyzing = false;
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate energy in different frequency bands
   */
  calculateEnergyBands(channelData, sampleRate) {
    const windowSize = 2048;
    const hopSize = 512;
    const numWindows = Math.floor((channelData.length - windowSize) / hopSize);
    
    const lowEnergy = new Float32Array(numWindows);
    const midEnergy = new Float32Array(numWindows);
    const highEnergy = new Float32Array(numWindows);
    
    // Process audio in windows
    for (let i = 0; i < numWindows; i++) {
      const start = i * hopSize;
      const end = start + windowSize;
      
      let lowSum = 0, midSum = 0, highSum = 0;
      
      // Simple energy calculation (sum of squares)
      for (let j = start; j < end && j < channelData.length; j++) {
        const sample = channelData[j];
        const energy = sample * sample;
        
        // Rough frequency band separation based on position
        const pos = (j - start) / windowSize;
        if (pos < 0.2) {
          lowSum += energy; // Low frequencies (kick)
        } else if (pos < 0.6) {
          midSum += energy; // Mid frequencies (snare)
        } else {
          highSum += energy; // High frequencies (hi-hat)
        }
      }
      
      lowEnergy[i] = lowSum;
      midEnergy[i] = midSum;
      highEnergy[i] = highSum;
    }
    
    return { lowEnergy, midEnergy, highEnergy };
  }

  /**
   * Detect kick drum hits (low frequency peaks)
   */
  detectKicks(lowEnergy, sampleRate, beatLength) {
    const kicks = [];
    const threshold = this.calculateAdaptiveThreshold(lowEnergy, 1.5);
    const minDistance = beatLength * 0.3; // Minimum 30% of beat length between kicks
    const hopSize = 512;
    const timePerWindow = hopSize / sampleRate;
    
    let lastKickTime = -999;
    
    for (let i = 1; i < lowEnergy.length - 1; i++) {
      const time = i * timePerWindow;
      const isPeak = lowEnergy[i] > lowEnergy[i - 1] && lowEnergy[i] > lowEnergy[i + 1];
      const isLoud = lowEnergy[i] > threshold;
      const isDistant = (time - lastKickTime) > minDistance;
      
      if (isP && isLoud && isDistant) {
        kicks.push(time);
        lastKickTime = time;
      }
    }
    
    return kicks;
  }

  /**
   * Detect snare hits (mid-high frequency peaks)
   */
  detectSnares(midEnergy, highEnergy, sampleRate, beatLength) {
    const snares = [];
    const combinedEnergy = new Float32Array(midEnergy.length);
    
    // Combine mid and high energy for snare detection
    for (let i = 0; i < midEnergy.length; i++) {
      combinedEnergy[i] = midEnergy[i] * 0.6 + highEnergy[i] * 0.4;
    }
    
    const threshold = this.calculateAdaptiveThreshold(combinedEnergy, 1.3);
    const minDistance = beatLength * 0.3;
    const hopSize = 512;
    const timePerWindow = hopSize / sampleRate;
    
    let lastSnareTime = -999;
    
    for (let i = 1; i < combinedEnergy.length - 1; i++) {
      const time = i * timePerWindow;
      const isPeak = combinedEnergy[i] > combinedEnergy[i - 1] && combinedEnergy[i] > combinedEnergy[i + 1];
      const isLoud = combinedEnergy[i] > threshold;
      const isDistant = (time - lastSnareTime) > minDistance;
      
      if (isPeak && isLoud && isDistant) {
        snares.push(time);
        lastSnareTime = time;
      }
    }
    
    return snares;
  }

  /**
   * Calculate adaptive threshold based on energy distribution
   */
  calculateAdaptiveThreshold(energy, multiplier = 1.5) {
    // Calculate mean
    let sum = 0;
    for (let i = 0; i < energy.length; i++) {
      sum += energy[i];
    }
    const mean = sum / energy.length;
    
    // Calculate standard deviation
    let variance = 0;
    for (let i = 0; i < energy.length; i++) {
      variance += Math.pow(energy[i] - mean, 2);
    }
    const stdDev = Math.sqrt(variance / energy.length);
    
    return mean + (stdDev * multiplier);
  }

  /**
   * Create a regular beat grid aligned with detected kicks
   */
  createBeatGrid(kickTimes, beatLength, duration) {
    if (kickTimes.length === 0) {
      // No kicks detected, create grid from BPM
      const grid = [];
      for (let t = 0; t < duration; t += beatLength) {
        grid.push(t);
      }
      return grid;
    }
    
    // Find the average offset of kicks from regular grid
    let offsetSum = 0;
    let count = 0;
    
    for (const kickTime of kickTimes) {
      const expectedBeat = Math.round(kickTime / beatLength);
      const expectedTime = expectedBeat * beatLength;
      const offset = kickTime - expectedTime;
      offsetSum += offset;
      count++;
    }
    
    const avgOffset = offsetSum / count;
    console.log(`  📍 Average kick offset: ${(avgOffset * 1000).toFixed(1)}ms`);
    
    // Create grid with offset
    const grid = [];
    for (let beat = 0; beat * beatLength < duration; beat++) {
      grid.push(beat * beatLength + avgOffset);
    }
    
    return grid;
  }

  /**
   * Find the nearest beat to a given time
   */
  findNearestBeat(time) {
    if (this.beatGrid.length === 0) return time;
    
    let nearest = this.beatGrid[0];
    let minDiff = Math.abs(time - nearest);
    
    for (const beatTime of this.beatGrid) {
      const diff = Math.abs(time - beatTime);
      if (diff < minDiff) {
        minDiff = diff;
        nearest = beatTime;
      }
    }
    
    return nearest;
  }

  /**
   * Get beat phase at specific time (0.0 = on beat, 0.5 = halfway between beats)
   */
  getBeatPhase(time) {
    if (this.beatGrid.length < 2) return 0;
    
    // Find surrounding beats
    let prevBeat = this.beatGrid[0];
    let nextBeat = this.beatGrid[1];
    
    for (let i = 0; i < this.beatGrid.length - 1; i++) {
      if (time >= this.beatGrid[i] && time < this.beatGrid[i + 1]) {
        prevBeat = this.beatGrid[i];
        nextBeat = this.beatGrid[i + 1];
        break;
      }
    }
    
    const beatLength = nextBeat - prevBeat;
    const phase = (time - prevBeat) / beatLength;
    
    return phase;
  }

  /**
   * Snap time to nearest beat
   */
  snapToBeat(time, snapStrength = 1.0) {
    const nearestBeat = this.findNearestBeat(time);
    return time + (nearestBeat - time) * snapStrength;
  }

  /**
   * Check if time is close to a kick
   */
  isNearKick(time, tolerance = 0.05) {
    return this.kickTimes.some(kickTime => Math.abs(time - kickTime) < tolerance);
  }

  /**
   * Check if time is close to a snare
   */
  isNearSnare(time, tolerance = 0.05) {
    return this.snareTimes.some(snareTime => Math.abs(time - snareTime) < tolerance);
  }
}

export default BeatDetector;
